import React, { useEffect, useMemo, useState } from "react";
import { I } from "../redesign/kvpIcons";
import { KontaktSuchfeld } from "../forms/widgets/KontaktSuchfeld";
import { VORGANG_ARTEN } from "../../data/vorgangArt";
import { listAppUsers, userDisplay, type AppUser } from "../../services/userService";
import { FELD_LABEL, type RegelBedingung, type RegelFeld } from "../../services/mailVorgangRegelService";
import {
    MAX_BEDINGUNGEN,
    betreffVorschlag,
    leereBedingung,
    regelKlartext,
    type RegelEntwurf,
    type RegelZielVorgang,
} from "./mailRegel";
import { MailRegelVorgangSuche } from "./MailRegelVorgangSuche";
import "./mailSettings.css";

/**
 * Das Formular EINER Mail-Regel (Cluster REGELN, H1–H4).
 *
 * FLUENT-FREI im Sinne der Projektregel: ausschließlich `.mset-*`-Klassen aus
 * `mailSettings.css` und native Formularelemente. Die einzige eingebundene
 * Fremdkomponente ist `KontaktSuchfeld` (Cluster ZUWEISEN) — die nutzt zwar
 * Fluents Styling-Engine `makeStyles`, aber keine Fluent-Formularkomponente,
 * und ist der Hausstandard der `forms/`-Bausteine.
 */

const FELDER: RegelFeld[] = ["absender", "domain", "betreff", "inhalt", "zeitraum"];

const PLATZHALTER: Record<Exclude<RegelFeld, "zeitraum">, string> = {
    absender: "name@firma.de",
    domain: "firma.de",
    betreff: "z. B. Schaden 4711",
    inhalt: "Wort oder Wendung im Mailtext",
};

type Props = {
    entwurf: RegelEntwurf;
    onChange: (naechster: RegelEntwurf) => void;
    /**
     * Aus einem Vorgang heraus geöffnet: das Ziel steht fest und die zweite
     * Aktion entfällt — sonst könnte man aus dem Vorgang heraus eine Regel
     * bauen, die mit ihm nichts mehr zu tun hat.
     */
    vorgangFixiert?: RegelZielVorgang | null;
    /** Auswählbare Postfächer für „Nur im Postfach". */
    postfaecher: string[];
    /** Betreff der auslösenden Mail (anklickbarer Vorschlag), falls vorhanden. */
    betreffQuelle?: string | null;
};

export const MailRegelForm: React.FC<Props> = ({
    entwurf,
    onChange,
    vorgangFixiert,
    postfaecher,
    betreffQuelle,
}) => {
    const [users, setUsers] = useState<AppUser[]>([]);

    useEffect(() => {
        let lebt = true;
        void listAppUsers()
            .then((list) => lebt && setUsers(list))
            .catch(() => undefined);
        return () => {
            lebt = false;
        };
    }, []);

    const setze = (teil: Partial<RegelEntwurf>) => onChange({ ...entwurf, ...teil });

    const setzeBedingung = (index: number, b: RegelBedingung) => {
        const naechste = entwurf.bedingungen.slice();
        naechste[index] = b;
        setze({ bedingungen: naechste });
    };

    const entferneBedingung = (index: number) => {
        const naechste = entwurf.bedingungen.filter((_, i) => i !== index);
        // Nie ganz leer — sonst steht das Formular ohne Eingabezeile da.
        setze({ bedingungen: naechste.length ? naechste : [leereBedingung("absender")] });
    };

    const wechsleFeld = (index: number, feld: RegelFeld) => {
        const alt = entwurf.bedingungen[index];
        // Wert nur übernehmen, wenn beide Seiten Textfelder sind.
        const wert = alt && alt.feld !== "zeitraum" ? alt.wert : "";
        setzeBedingung(index, feld === "zeitraum" ? leereBedingung("zeitraum") : { feld, wert });
    };

    const klartext = useMemo(() => regelKlartext(entwurf), [entwurf]);
    const vorschlag = betreffQuelle ? betreffVorschlag(betreffQuelle) : "";
    const neuerVorgang = entwurf.aktion === "neuer_vorgang";

    return (
        <div className="mset-card">
            {/* ── Name ─────────────────────────────────────────────────── */}
            <div>
                <label className="mset-label">Name der Regel (optional)</label>
                <input
                    className="mset-inp"
                    value={entwurf.name}
                    placeholder={klartext}
                    onChange={(e) => setze({ name: e.target.value })}
                />
            </div>

            {/* ── Bedingungen ──────────────────────────────────────────── */}
            <div className="mset-block">
                <label className="mset-label">Bedingungen</label>
                <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
                    <label className="mset-radio">
                        <input
                            type="radio"
                            name="mset-verknuepfung"
                            checked={entwurf.verknuepfung === "und"}
                            onChange={() => setze({ verknuepfung: "und" })}
                        />
                        Alle müssen zutreffen (und)
                    </label>
                    <label className="mset-radio">
                        <input
                            type="radio"
                            name="mset-verknuepfung"
                            checked={entwurf.verknuepfung === "oder"}
                            onChange={() => setze({ verknuepfung: "oder" })}
                        />
                        Eine genügt (oder)
                    </label>
                </div>

                {entwurf.bedingungen.map((b, i) => (
                    <div className="mset-cond" key={`${b.feld}-${i}`}>
                        <select
                            className="mset-sel"
                            style={{ width: 190 }}
                            value={b.feld}
                            onChange={(e) => wechsleFeld(i, e.target.value as RegelFeld)}
                            aria-label={`Bedingung ${i + 1}`}
                        >
                            {FELDER.map((f) => (
                                <option key={f} value={f}>
                                    {FELD_LABEL[f]}
                                </option>
                            ))}
                        </select>

                        {b.feld === "zeitraum" ? (
                            <div className="mset-cond-dates">
                                <input
                                    className="mset-inp"
                                    type="date"
                                    value={b.von ?? ""}
                                    onChange={(e) =>
                                        setzeBedingung(i, {
                                            feld: "zeitraum",
                                            von: e.target.value || null,
                                            bis: b.bis,
                                        })
                                    }
                                    aria-label="Empfangen ab"
                                />
                                <span className="mset-hint" style={{ margin: 0 }}>
                                    bis
                                </span>
                                <input
                                    className="mset-inp"
                                    type="date"
                                    value={b.bis ?? ""}
                                    onChange={(e) =>
                                        setzeBedingung(i, {
                                            feld: "zeitraum",
                                            von: b.von,
                                            bis: e.target.value || null,
                                        })
                                    }
                                    aria-label="Empfangen bis"
                                />
                            </div>
                        ) : (
                            <input
                                className="mset-inp"
                                value={b.wert}
                                placeholder={PLATZHALTER[b.feld]}
                                onChange={(e) => setzeBedingung(i, { feld: b.feld, wert: e.target.value })}
                                aria-label={`Wert der Bedingung ${i + 1}`}
                            />
                        )}

                        <button
                            type="button"
                            className="mset-btn mset-cond-x"
                            title="Bedingung entfernen"
                            aria-label={`Bedingung ${i + 1} entfernen`}
                            onClick={() => entferneBedingung(i)}
                        >
                            ×
                        </button>
                    </div>
                ))}

                <div className="mset-actions" style={{ marginTop: 8 }}>
                    <button
                        type="button"
                        className="mset-btn"
                        disabled={entwurf.bedingungen.length >= MAX_BEDINGUNGEN}
                        onClick={() =>
                            setze({ bedingungen: [...entwurf.bedingungen, leereBedingung("betreff")] })
                        }
                    >
                        + Bedingung hinzufügen
                    </button>
                    {vorschlag && (
                        <button
                            type="button"
                            className="mset-btn"
                            title="Betreff der angeklickten Mail als Bedingung übernehmen"
                            onClick={() =>
                                setze({
                                    bedingungen: [
                                        ...entwurf.bedingungen,
                                        { feld: "betreff", wert: vorschlag },
                                    ],
                                })
                            }
                        >
                            Betreff „{vorschlag}" übernehmen
                        </button>
                    )}
                </div>
                {entwurf.bedingungen.length >= MAX_BEDINGUNGEN && (
                    <div className="mset-hint">Mehr als {MAX_BEDINGUNGEN} Bedingungen je Regel sind nicht möglich.</div>
                )}
            </div>

            {/* ── Postfach ─────────────────────────────────────────────── */}
            <div className="mset-block">
                <label className="mset-cbx">
                    <input
                        type="checkbox"
                        checked={!!entwurf.mailbox}
                        onChange={(e) =>
                            setze({ mailbox: e.target.checked ? (postfaecher[0] ?? null) : null })
                        }
                    />
                    <span className="mset-cbx-box">{entwurf.mailbox ? I.check : null}</span>
                    Nur im Postfach
                </label>
                {entwurf.mailbox && (
                    <select
                        className="mset-sel"
                        style={{ marginTop: 6 }}
                        value={entwurf.mailbox}
                        onChange={(e) => setze({ mailbox: e.target.value })}
                        aria-label="Postfach"
                    >
                        {/* Auch ein Postfach aufnehmen, das nicht (mehr) in der Liste
                            steht — sonst spränge die Auswahl beim Bearbeiten um. */}
                        {(postfaecher.includes(entwurf.mailbox)
                            ? postfaecher
                            : [entwurf.mailbox, ...postfaecher]
                        ).map((mb) => (
                            <option key={mb} value={mb}>
                                {mb}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            {/* ── Aktion ───────────────────────────────────────────────── */}
            <div className="mset-block">
                <label className="mset-label">Dann …</label>

                <label className="mset-radio">
                    <input
                        type="radio"
                        name="mset-aktion"
                        checked={!neuerVorgang}
                        onChange={() => setze({ aktion: "zuweisen" })}
                    />
                    Diesem Vorgang zuweisen
                </label>
                {!neuerVorgang &&
                    (vorgangFixiert ? (
                        <div style={{ marginTop: 6 }}>
                            <span className="mset-badge">{vorgangFixiert.label}</span>
                        </div>
                    ) : (
                        <MailRegelVorgangSuche
                            value={entwurf.ziel}
                            onChange={(ziel) => setze({ ziel })}
                        />
                    ))}

                <label className={`mset-radio${vorgangFixiert ? " disabled" : ""}`}>
                    <input
                        type="radio"
                        name="mset-aktion"
                        checked={neuerVorgang}
                        disabled={!!vorgangFixiert}
                        onChange={() => setze({ aktion: "neuer_vorgang" })}
                    />
                    Neuen Vorgang erstellen
                </label>

                {neuerVorgang && (
                    <div className="mset-sub">
                        <div className="mset-block" style={{ marginTop: 0 }}>
                            <label className="mset-label">Vorgangsart</label>
                            <select
                                className="mset-sel"
                                value={entwurf.aktionVorgangsart ?? ""}
                                onChange={(e) => setze({ aktionVorgangsart: e.target.value || null })}
                            >
                                <option value="">automatisch (Interessent → NKG, Kunde → GAB)</option>
                                {VORGANG_ARTEN.map((a) => (
                                    <option key={a} value={a}>
                                        {a}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mset-block">
                            <label className="mset-label">Betreff</label>
                            <label className="mset-radio">
                                <input
                                    type="radio"
                                    name="mset-betreff"
                                    checked={entwurf.aktionBetreffQuelle === "mail"}
                                    onChange={() => setze({ aktionBetreffQuelle: "mail" })}
                                />
                                aus der Mail übernehmen (ohne Re:/AW:/WG:)
                            </label>
                            <label className="mset-radio">
                                <input
                                    type="radio"
                                    name="mset-betreff"
                                    checked={entwurf.aktionBetreffQuelle === "fest"}
                                    onChange={() => setze({ aktionBetreffQuelle: "fest" })}
                                />
                                fester Text
                            </label>
                            {entwurf.aktionBetreffQuelle === "fest" && (
                                <input
                                    className="mset-inp"
                                    style={{ marginTop: 6 }}
                                    value={entwurf.aktionBetreff}
                                    placeholder="z. B. Sammelvorgang Allianz"
                                    onChange={(e) => setze({ aktionBetreff: e.target.value })}
                                    aria-label="Fester Betreff"
                                />
                            )}
                        </div>

                        <div className="mset-block">
                            <label className="mset-label">Kontakt</label>
                            <label className="mset-radio">
                                <input
                                    type="radio"
                                    name="mset-kontakt"
                                    checked={entwurf.aktionKontaktQuelle === "erkannt"}
                                    onChange={() => setze({ aktionKontaktQuelle: "erkannt" })}
                                />
                                aus der Mail erkannt
                            </label>
                            <label className="mset-radio">
                                <input
                                    type="radio"
                                    name="mset-kontakt"
                                    checked={entwurf.aktionKontaktQuelle === "fest"}
                                    onChange={() => setze({ aktionKontaktQuelle: "fest" })}
                                />
                                fester Kontakt
                            </label>
                            {entwurf.aktionKontaktQuelle === "fest" && (
                                <div style={{ marginTop: 6 }}>
                                    <KontaktSuchfeld
                                        label="Kontakt"
                                        name="regelKontakt"
                                        placeholder="Firma oder Person suchen"
                                        value={
                                            entwurf.aktionKontaktId
                                                ? {
                                                      contactId: entwurf.aktionKontaktId,
                                                      displayName:
                                                          entwurf.aktionKontaktLabel ??
                                                          `Kontakt #${entwurf.aktionKontaktId}`,
                                                      contactType: "Unknown",
                                                      art: "sonstige",
                                                      statusName: null,
                                                      companyId: null,
                                                      companyName: null,
                                                      defaultEmail: null,
                                                      contactNbrInt: null,
                                                  }
                                                : null
                                        }
                                        onChange={(t) =>
                                            setze({
                                                aktionKontaktId: t?.contactId ?? null,
                                                aktionKontaktLabel: t?.displayName ?? null,
                                            })
                                        }
                                    />
                                </div>
                            )}
                            <label className="mset-radio">
                                <input
                                    type="radio"
                                    name="mset-kontakt"
                                    checked={entwurf.aktionKontaktQuelle === "ohne"}
                                    onChange={() => setze({ aktionKontaktQuelle: "ohne" })}
                                />
                                ohne Kontakt
                            </label>
                        </div>

                        <div className="mset-block">
                            <label className="mset-label">Hauptverantwortliche</label>
                            <select
                                className="mset-sel"
                                multiple
                                size={Math.min(6, Math.max(3, users.length))}
                                value={entwurf.aktionHauptverantwortlich}
                                onChange={(e) =>
                                    setze({
                                        aktionHauptverantwortlich: Array.from(
                                            e.target.selectedOptions,
                                        ).map((o) => o.value),
                                    })
                                }
                                aria-label="Hauptverantwortliche"
                            >
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {userDisplay(u)}
                                    </option>
                                ))}
                            </select>
                            <div className="mset-hint">
                                Ohne Auswahl wird der Vorgang Ihnen zugeordnet.
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Live-Klartext ────────────────────────────────────────── */}
            <div className="mset-hint" style={{ marginTop: 12 }}>
                {klartext}
            </div>
        </div>
    );
};

export default MailRegelForm;
